import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; 
import yocoLogo from '../assets/images/yoco.jpg';
import ozowLogo from '../assets/images/Ozow.jpg';
import './Paymentmodal.css';

const API_BASE = 'https://mykaddle-api.onrender.com/api/payments';

// ── Fee calculation (mirrors backend) ───────────────────────
const calcFees = (baseAmount) => {
  const amount       = parseFloat(baseAmount) || 0;
  const serviceFee   = parseFloat(Math.min(amount * 0.05, 30).toFixed(2));
  const platformFee  = parseFloat((amount * 0.15).toFixed(2));
  const tutorPayout  = parseFloat((amount - platformFee).toFixed(2));
  const totalCharged = parseFloat((amount + serviceFee).toFixed(2));
  return { amount, serviceFee, platformFee, tutorPayout, totalCharged };
};

// ── Chevron Icon ───────────────────────────────────────────
const ChevronIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 24" fill="none">
    <path fill="currentColor" fillRule="evenodd" d="M2 19.438L3.045 20.5l7.666-7.79a1.02 1.02 0 0 0 0-1.42L3.045 3.5L2 4.563L9.318 12z"/>
  </svg>
);

// ── Main Payment Page ──────────────────────────────────────
const PaymentModal = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Read data from location.state (passed via navigate)
  const {
    subjectId = '',
    subjectName = 'Subject',
    subscriptionAmount = 0,
    billingCycle = 'monthly',
  } = location.state || {};

  const fees = calcFees(subscriptionAmount);

  const [step, setStep]             = useState('review');
  const [errorMsg, setErrorMsg]     = useState('');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerLabel, setProviderLabel] = useState('');

  // Redirect if no data
  useEffect(() => {
    if (!location.state || !subjectId) {
      navigate(-1);
    }
  }, [location.state, subjectId, navigate]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && step === 'review') handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [step]);

  const handleClose = () => {
    navigate(-1);
  };

  // ── Click provider → pay immediately ──
  const handlePay = async (provider) => {
    setSelectedProvider(provider);
    setProviderLabel(provider === 'yoco' ? 'Yoco' : 'Ozow');
    setStep('processing');
    setErrorMsg('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          subjectId,
          subscriptionAmount: fees.amount,
          billingCycle,
          provider,
          // Pass these so the backend knows where to redirect
          successUrl: `${window.location.origin}/payment/success?provider=${provider}`,
          cancelUrl: `${window.location.origin}/payment/cancel?provider=${provider}`,
          errorUrl: `${window.location.origin}/payment/error?provider=${provider}`,
        }),
      });

      const text = await res.text();
      let data = {};
      try {
        data = JSON.parse(text);
      } catch {
        setErrorMsg(
          res.status === 404
            ? 'Payment endpoint not found (404). Ensure /api/payments is registered in your Express app.'
            : `Server error ${res.status}. Please try again.`
        );
        setStep('error');
        return;
      }

      if (!res.ok) {
        setErrorMsg(data.message || 'Payment initiation failed. Please try again.');
        setStep('error');
        return;
      }

      if (data.paymentId) {
        sessionStorage.setItem('pendingPaymentId', data.paymentId);
        sessionStorage.setItem('pendingPaymentProvider', data.provider || provider);
      }

      setStep('redirecting');
      setTimeout(() => {
        window.location.href = data.paymentUrl;
      }, 500);

    } catch (err) {
      console.error('Payment error:', err);
      setErrorMsg('Something went wrong. Please check your connection and try again.');
      setStep('error');
    }
  };

  return (
    <div className="pay-dashboard-container">
      <div className="pay-almighty-wrapper">

        {/* Top bar */}
        <div className="pay-top-bar">
          <div className="pay-top-left-header">
            <div className="pay-header-row">
              <button className="pay-back-icon-btn" aria-label="Go back" onClick={handleClose}>
                <svg xmlns="http://www.w3.org/2000/svg" width="0.5em" height="1em" viewBox="0 0 12 24">
                  <path d="M0 0h12v24H0z" fill="none"/>
                  <path fill="currentColor" fillRule="evenodd" d="M10 19.438L8.955 20.5l-7.666-7.79a1.02 1.02 0 0 1 0-1.42L8.955 3.5L10 4.563L2.682 12z"/>
                </svg>
              </button>
              <span className="pay-page-title">Payment</span>
            </div>
            <p className="pay-sub-header">Choose a payment option below from our partnered service providers to proceed.</p>
          </div>
        </div>

        {/* Payment options */}
        <div className="pay-payment-body">
          {step === 'review' && (
            <>
              <div className="pay-cal">
                <div className="pay-head">Once-off <span className="pay-highlight">{billingCycle === 'weekly' ? 'Weekly' : 'Monthly'}</span> Subscription to {subjectName}</div>
                <div className="pay-subhead1">Total amount R {fees.amount.toFixed(2)}</div>
                <div className="pay-subhead2">Charged, service fee included <span className="pay-highlight">R {fees.totalCharged.toFixed(2)}</span></div>
                <div className="pay-service-fee">*Standard service fee (5%, max R30)</div>
              </div>

              {/* Yoco */}
              <button
                className="pay-option"
                onClick={() => handlePay('yoco')}
              >
                <div className="pay-provider-logo">
                  <img src={yocoLogo} alt="Yoco" />
                </div>
                <div className="pay-provider-info">
                  <span className="pay-provider-name">Yoco</span>
                  <span className="pay-provider-desc">Secure card payments and more</span>
                  <div className="pay-provider-chips">
                    <span className="pay-chip pay-chip-purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="1.29em" height="1em" viewBox="0 0 256 199">
                        <path d="M0 0h256v199H0z" fill="none" />
                        <path d="M46.54 198.011V184.84c0-5.05-3.074-8.342-8.343-8.342c-2.634 0-5.488.878-7.464 3.732c-1.536-2.415-3.731-3.732-7.024-3.732c-2.196 0-4.39.658-6.147 3.073v-2.634h-4.61v21.074h4.61v-11.635c0-3.731 1.976-5.488 5.05-5.488c3.072 0 4.61 1.976 4.61 5.488v11.635h4.61v-11.635c0-3.731 2.194-5.488 5.048-5.488c3.074 0 4.61 1.976 4.61 5.488v11.635zm68.271-21.074h-7.463v-6.366h-4.61v6.366h-4.171v4.17h4.17v9.66c0 4.83 1.976 7.683 7.245 7.683c1.976 0 4.17-.658 5.708-1.536l-1.318-3.952c-1.317.878-2.853 1.098-3.951 1.098c-2.195 0-3.073-1.317-3.073-3.513v-9.44h7.463zm39.076-.44c-2.634 0-4.39 1.318-5.488 3.074v-2.634h-4.61v21.074h4.61v-11.854c0-3.512 1.536-5.488 4.39-5.488c.878 0 1.976.22 2.854.439l1.317-4.39c-.878-.22-2.195-.22-3.073-.22m-59.052 2.196c-2.196-1.537-5.269-2.195-8.562-2.195c-5.268 0-8.78 2.634-8.78 6.805c0 3.513 2.634 5.488 7.244 6.147l2.195.22c2.415.438 3.732 1.097 3.732 2.195c0 1.536-1.756 2.634-4.83 2.634s-5.488-1.098-7.025-2.195l-2.195 3.512c2.415 1.756 5.708 2.634 9 2.634c6.147 0 9.66-2.853 9.66-6.805c0-3.732-2.854-5.708-7.245-6.366l-2.195-.22c-1.976-.22-3.512-.658-3.512-1.975c0-1.537 1.536-2.415 3.951-2.415c2.635 0 5.269 1.097 6.586 1.756z" />
                        <path fill="#ff5f00" d="M93.298 16.903h69.15v124.251h-69.15z" />
                        <path fill="#eb001b" d="M97.689 79.029c0-25.245 11.854-47.637 30.074-62.126C114.373 6.366 97.47 0 79.03 0C35.343 0 0 35.343 0 79.029s35.343 79.029 79.029 79.029c18.44 0 35.343-6.366 48.734-16.904c-18.22-14.269-30.074-36.88-30.074-62.125" />
                        <path fill="#f79e1b" d="M255.746 79.029c0 43.685-35.343 79.029-79.029 79.029c-18.44 0-35.343-6.366-48.734-16.904c18.44-14.488 30.075-36.88 30.075-62.125s-11.855-47.637-30.075-62.126C141.373 6.366 158.277 0 176.717 0c43.686 0 79.03 35.563 79.03 79.029" />
                      </svg>
                      Card Payments
                    </span>
                    <span className="pay-chip pay-chip-purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 128 128">
                        <path d="M0 0h128v128H0z" fill="none" />
                        <path d="M97.905 67.885c.174 18.8 16.494 25.057 16.674 25.137c-.138.44-2.607 8.916-8.597 17.669c-5.178 7.568-10.553 15.108-19.018 15.266c-8.318.152-10.993-4.934-20.504-4.934c-9.508 0-12.479 4.776-20.354 5.086c-8.172.31-14.395-8.185-19.616-15.724C15.822 94.961 7.669 66.8 18.616 47.791c5.438-9.44 15.158-15.417 25.707-15.571c8.024-.153 15.598 5.398 20.503 5.398c4.902 0 14.106-6.676 23.782-5.696c4.051.169 15.421 1.636 22.722 12.324c-.587.365-13.566 7.921-13.425 23.639M82.272 21.719c4.338-5.251 7.258-12.563 6.462-19.836c-6.254.251-13.816 4.167-18.301 9.416c-4.02 4.647-7.54 12.087-6.591 19.216c6.971.54 14.091-3.542 18.43-8.796" />
                      </svg>
                      Apple Pay
                    </span>
                    <span className="pay-chip pay-chip-purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 128 128">
                        <path d="M0 0h128v128H0z" fill="none" />
                        <path fill="#fff" d="M44.59 4.21a63.28 63.28 0 0 0 4.33 120.9a67.6 67.6 0 0 0 32.36.35a57.13 57.13 0 0 0 25.9-13.46a57.44 57.44 0 0 0 16-26.26a74.3 74.3 0 0 0 1.61-33.58H65.27v24.69h34.47a29.72 29.72 0 0 1-12.66 19.52a36.2 36.2 0 0 1-13.93 5.5a41.3 41.3 0 0 1-15.1 0A37.2 37.2 0 0 1 44 95.74a39.3 39.3 0 0 1-14.5-19.42a38.3 38.3 0 0 1 0-24.63a39.25 39.25 0 0 1 9.18-14.91A37.17 37.17 0 0 1 76.13 27a34.3 34.3 0 0 1 13.64 8q5.83-5.8 11.64-11.63c2-2.09 4.18-4.08 6.15-6.22A61.2 61.2 0 0 0 87.2 4.59a64 64 0 0 0-42.61-.38" />
                        <path fill="#e33629" d="M44.59 4.21a64 64 0 0 1 42.61.37a61.2 61.2 0 0 1 20.35 12.62c-2 2.14-4.11 4.14-6.15 6.22Q95.58 29.23 89.77 35a34.3 34.3 0 0 0-13.64-8a37.17 37.17 0 0 0-37.46 9.74a39.25 39.25 0 0 0-9.18 14.91L8.76 35.6A63.53 63.53 0 0 1 44.59 4.21" />
                        <path fill="#f8bd00" d="M3.26 51.5a63 63 0 0 1 5.5-15.9l20.73 16.09a38.3 38.3 0 0 0 0 24.63q-10.36 8-20.73 16.08a63.33 63.33 0 0 1-5.5-40.9" />
                        <path fill="#587dbd" d="M65.27 52.15h59.52a74.3 74.3 0 0 1-1.61 33.58a57.44 57.44 0 0 1-16 26.26c-6.69-5.22-13.41-10.4-20.1-15.62a29.72 29.72 0 0 0 12.66-19.54H65.27c-.01-8.22 0-16.45 0-24.68" />
                        <path fill="#319f43" d="M8.75 92.4q10.37-8 20.73-16.08A39.3 39.3 0 0 0 44 95.74a37.2 37.2 0 0 0 14.08 6.08a41.3 41.3 0 0 0 15.1 0a36.2 36.2 0 0 0 13.93-5.5c6.69 5.22 13.41 10.4 20.1 15.62a57.13 57.13 0 0 1-25.9 13.47a67.6 67.6 0 0 1-32.36-.35a63 63 0 0 1-23-11.59A63.7 63.7 0 0 1 8.75 92.4" />
                      </svg>
                      Google Pay
                    </span>
                  </div>
                </div>
                <span className="pay-provider-chevron"><ChevronIcon /></span>
              </button>

              {/* Ozow */}
              <button
                className="pay-option"
                onClick={() => handlePay('ozow')}
              >
                <div className="pay-provider-logo">
                  <img src={ozowLogo} alt="Ozow" />
                </div>
                <div className="pay-provider-info">
                  <span className="pay-provider-name">Ozow</span>
                  <span className="pay-provider-desc">Instant secure EFT</span>
                  <div className="pay-provider-chips">
                    <span className="pay-chip pay-chip-green">Instant EFT</span>
                    <span className="pay-chip pay-chip-green">Multiple SA Banks</span>
                    <span className="pay-chip pay-chip-gray">No card needed</span>
                  </div>
                </div>
                <span className="pay-provider-chevron"><ChevronIcon /></span>
              </button>

              {fees.amount <= 0 && (
                <p className="pay-amount-error">Invalid subscription amount. Please go back and try again.</p>
              )}
            </>
          )}

          {step === 'processing' && (
            <div className="pay-state" style={{ marginTop: '1rem' }}>
              <div className="pay-state-spinner" />
              <p className="pay-state-title">Preparing your payment…</p>
              <p className="pay-state-sub">Please wait, do not close this window.</p>
            </div>
          )}

          {step === 'redirecting' && (
            <div className="pay-state" style={{ marginTop: '1rem' }}>
              <div className="pay-state-spinner" />
              <p className="pay-state-title">Redirecting to {providerLabel}…</p>
              <p className="pay-state-sub">You will be taken to the {providerLabel} payment page.</p>
            </div>
          )}

          {step === 'error' && (
            <div className="pay-state" style={{ marginTop: '1rem' }}>
              <div className="pay-error-icon">⚠</div>
              <p className="pay-state-title">Payment failed</p>
              <p className="pay-error-msg">{errorMsg}</p>
              <div className="pay-error-actions">
                <button className="pay-retry-btn" onClick={() => setStep('review')}>Try again</button>
                <button className="pay-cancel-btn" onClick={handleClose}>Cancel</button>
              </div>
            </div>
          )}
        </div>

        {/* Info bar */}
        <div className="pay-info-bar">
          <div className="pay-info-bar-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
              <path d="M0 0h24v24H0z" fill="none"/>
              <path fill="#d7df23" d="M12 2.5c-3.81 0-6.5 2.743-6.5 6.119c0 1.536.632 2.572 1.425 3.56c.172.215.347.422.527.635l.096.112c.21.25.427.508.63.774c.404.531.783 1.128.995 1.834a.75.75 0 0 1-1.436.432c-.138-.46-.397-.89-.753-1.357a18 18 0 0 0-.582-.714l-.092-.11c-.18-.212-.37-.436-.555-.667C4.87 12.016 4 10.651 4 8.618C4 4.363 7.415 1 12 1s8 3.362 8 7.619c0 2.032-.87 3.397-1.755 4.5c-.185.23-.375.454-.555.667l-.092.109c-.21.248-.405.481-.582.714c-.356.467-.615.898-.753 1.357a.751.751 0 0 1-1.437-.432c.213-.706.592-1.303.997-1.834c.202-.266.419-.524.63-.774l.095-.112c.18-.213.355-.42.527-.634c.793-.99 1.425-2.025 1.425-3.561C18.5 5.243 15.81 2.5 12 2.5M8.75 18h6.5a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1 0-1.5m.75 3.75a.75.75 0 0 1 .75-.75h3.5a.75.75 0 0 1 0 1.5h-3.5a.75.75 0 0 1-.75-.75"/>
            </svg>
          </div>
          <p className="pay-info-bar-text">All payments at MyKaddle are handled by the payment processor — we don't store any card information.</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;